import { supabase } from "@/integrations/supabase/client";
import {
  InvoiceTemplate,
  InvoiceTemplateFormData,
  TemplateUpdateData,
  ValidationResult,
  ValidationError,
} from "@/types/invoice";
import { validateInvoiceTemplate } from "@/types/validation";

export class TemplateService {
  /**
   * Create a new invoice template
   */
  static async createTemplate(
    data: InvoiceTemplateFormData
  ): Promise<InvoiceTemplate> {
    try {
      // Validate template data
      const validation = this.validateTemplateData(data);
      if (!validation.isValid) {
        throw new Error(
          `Validation failed: ${validation.errors
            .map((e) => e.message)
            .join(", ")}`
        );
      }

      // If this is set as default, unset other defaults first
      if (data.isDefault) {
        await this.unsetDefaultTemplates();
      }

      const { data: insertedData, error } = await supabase
        .from("invoice_templates")
        .insert({
          name: data.name,
          description: data.description,
          is_default: data.isDefault,
          is_active: data.isActive,
          company_name: data.companyName,
          company_logo_url: data.companyLogoUrl,
          company_address: data.companyAddress,
          company_phone: data.companyPhone,
          company_email: data.companyEmail,
          company_website: data.companyWebsite,
          company_tax_id: data.companyTaxId,
          primary_color: data.primaryColor,
          secondary_color: data.secondaryColor,
          font_family: data.fontFamily,
          font_size: data.fontSize,
          show_logo: data.showLogo,
          show_company_info: data.showCompanyInfo,
          show_tax_breakdown: data.showTaxBreakdown,
          show_payment_terms: data.showPaymentTerms,
          show_notes: data.showNotes,
          custom_fields: data.customFields,
          footer_text: data.footerText,
          show_page_numbers: data.showPageNumbers,
        })
        .select()
        .single();

      if (error) throw error;

      return this.mapTemplateFromDB(insertedData);
    } catch (error) {
      console.error("Error creating template:", error);
      throw error;
    }
  }

  /**
   * Update an existing template
   */
  static async updateTemplate(
    id: string,
    data: Partial<InvoiceTemplateFormData>
  ): Promise<void> {
    try {
      // Validate template data if provided
      if (Object.keys(data).length > 0) {
        const validation = this.validateTemplateData(data, true);
        if (!validation.isValid) {
          throw new Error(
            `Validation failed: ${validation.errors
              .map((e) => e.message)
              .join(", ")}`
          );
        }
      }

      // If this is being set as default, unset other defaults first
      if (data.isDefault) {
        await this.unsetDefaultTemplates(id);
      }

      const updateData: any = {};

      if (data.name !== undefined) updateData.name = data.name;
      if (data.description !== undefined)
        updateData.description = data.description;
      if (data.isDefault !== undefined) updateData.is_default = data.isDefault;
      if (data.isActive !== undefined) updateData.is_active = data.isActive;
      if (data.companyName !== undefined)
        updateData.company_name = data.companyName;
      if (data.companyLogoUrl !== undefined)
        updateData.company_logo_url = data.companyLogoUrl;
      if (data.companyAddress !== undefined)
        updateData.company_address = data.companyAddress;
      if (data.companyPhone !== undefined)
        updateData.company_phone = data.companyPhone;
      if (data.companyEmail !== undefined)
        updateData.company_email = data.companyEmail;
      if (data.companyWebsite !== undefined)
        updateData.company_website = data.companyWebsite;
      if (data.companyTaxId !== undefined)
        updateData.company_tax_id = data.companyTaxId;
      if (data.primaryColor !== undefined)
        updateData.primary_color = data.primaryColor;
      if (data.secondaryColor !== undefined)
        updateData.secondary_color = data.secondaryColor;
      if (data.fontFamily !== undefined)
        updateData.font_family = data.fontFamily;
      if (data.fontSize !== undefined) updateData.font_size = data.fontSize;
      if (data.showLogo !== undefined) updateData.show_logo = data.showLogo;
      if (data.showCompanyInfo !== undefined)
        updateData.show_company_info = data.showCompanyInfo;
      if (data.showTaxBreakdown !== undefined)
        updateData.show_tax_breakdown = data.showTaxBreakdown;
      if (data.showPaymentTerms !== undefined)
        updateData.show_payment_terms = data.showPaymentTerms;
      if (data.showNotes !== undefined) updateData.show_notes = data.showNotes;
      if (data.customFields !== undefined)
        updateData.custom_fields = data.customFields;
      if (data.footerText !== undefined)
        updateData.footer_text = data.footerText;
      if (data.showPageNumbers !== undefined)
        updateData.show_page_numbers = data.showPageNumbers;

      updateData.updated_at = new Date().toISOString();

      const { error } = await supabase
        .from("invoice_templates")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;
    } catch (error) {
      console.error("Error updating template:", error);
      throw error;
    }
  }

  /**
   * Delete a template
   */
  static async deleteTemplate(id: string): Promise<void> {
    try {
      // Check if this is the default template
      const { data: template } = await supabase
        .from("invoice_templates")
        .select("is_default")
        .eq("id", id)
        .single();

      if (template?.is_default) {
        throw new Error("Cannot delete the default template");
      }

      // Check if template is in use by any invoices
      const { data: invoicesUsingTemplate } = await supabase
        .from("invoices")
        .select("id")
        .eq("template_id", id)
        .limit(1);

      if (invoicesUsingTemplate && invoicesUsingTemplate.length > 0) {
        throw new Error(
          "Cannot delete template that is being used by existing invoices"
        );
      }

      const { error } = await supabase
        .from("invoice_templates")
        .delete()
        .eq("id", id);

      if (error) throw error;
    } catch (error) {
      console.error("Error deleting template:", error);
      throw error;
    }
  }

  /**
   * Get all templates for a repair shop
   */
  static async getTemplates(repairShopId: string): Promise<InvoiceTemplate[]> {
    try {
      const { data, error } = await supabase
        .from("invoice_templates")
        .select("*")
        .eq("repair_shop_id", repairShopId)
        .order("is_default", { ascending: false })
        .order("name", { ascending: true });

      if (error) throw error;

      return data.map(this.mapTemplateFromDB);
    } catch (error) {
      console.error("Error fetching templates:", error);
      throw error;
    }
  }

  /**
   * Get a specific template by ID
   */
  static async getTemplate(id: string): Promise<InvoiceTemplate | null> {
    try {
      const { data, error } = await supabase
        .from("invoice_templates")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          return null; // Template not found
        }
        throw error;
      }

      return this.mapTemplateFromDB(data);
    } catch (error) {
      console.error("Error fetching template:", error);
      throw error;
    }
  }

  /**
   * Get the default template for a repair shop
   */
  static async getDefaultTemplate(
    repairShopId: string
  ): Promise<InvoiceTemplate | null> {
    try {
      const { data, error } = await supabase
        .from("invoice_templates")
        .select("*")
        .eq("repair_shop_id", repairShopId)
        .eq("is_default", true)
        .eq("is_active", true)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          // No default template found, create one
          return await this.createDefaultTemplate(repairShopId);
        }
        throw error;
      }

      return this.mapTemplateFromDB(data);
    } catch (error) {
      console.error("Error fetching default template:", error);
      throw error;
    }
  }

  /**
   * Clone an existing template
   */
  static async cloneTemplate(
    templateId: string,
    newName: string,
    setAsDefault: boolean = false
  ): Promise<InvoiceTemplate> {
    try {
      const originalTemplate = await this.getTemplate(templateId);
      if (!originalTemplate) {
        throw new Error("Template not found");
      }

      // Create new template data based on original
      const cloneData: InvoiceTemplateFormData = {
        name: newName,
        description: `Cloned from ${originalTemplate.name}`,
        isDefault: setAsDefault,
        isActive: true,
        companyName: originalTemplate.companyName,
        companyLogoUrl: originalTemplate.companyLogoUrl,
        companyAddress: originalTemplate.companyAddress,
        companyPhone: originalTemplate.companyPhone,
        companyEmail: originalTemplate.companyEmail,
        companyWebsite: originalTemplate.companyWebsite,
        companyTaxId: originalTemplate.companyTaxId,
        primaryColor: originalTemplate.primaryColor,
        secondaryColor: originalTemplate.secondaryColor,
        fontFamily: originalTemplate.fontFamily,
        fontSize: originalTemplate.fontSize,
        showLogo: originalTemplate.showLogo,
        showCompanyInfo: originalTemplate.showCompanyInfo,
        showTaxBreakdown: originalTemplate.showTaxBreakdown,
        showPaymentTerms: originalTemplate.showPaymentTerms,
        showNotes: originalTemplate.showNotes,
        customFields: [...originalTemplate.customFields], // Deep copy
        footerText: originalTemplate.footerText,
        showPageNumbers: originalTemplate.showPageNumbers,
      };

      return await this.createTemplate(cloneData);
    } catch (error) {
      console.error("Error cloning template:", error);
      throw error;
    }
  }

  /**
   * Create a default template for a repair shop
   */
  static async createDefaultTemplate(
    repairShopId: string
  ): Promise<InvoiceTemplate> {
    try {
      const defaultTemplateData: InvoiceTemplateFormData = {
        name: "Default Template",
        description: "Default invoice template",
        isDefault: true,
        isActive: true,
        companyName: "",
        companyLogoUrl: "",
        companyAddress: "",
        companyPhone: "",
        companyEmail: "",
        companyWebsite: "",
        companyTaxId: "",
        primaryColor: "#2563eb", // Blue
        secondaryColor: "#64748b", // Gray
        fontFamily: "Inter",
        fontSize: 12,
        showLogo: true,
        showCompanyInfo: true,
        showTaxBreakdown: true,
        showPaymentTerms: true,
        showNotes: true,
        customFields: [],
        footerText: "Thank you for your business!",
        showPageNumbers: true,
      };

      const { data: insertedData, error } = await supabase
        .from("invoice_templates")
        .insert({
          ...this.mapTemplateToDB(defaultTemplateData),
          repair_shop_id: repairShopId,
        })
        .select()
        .single();

      if (error) throw error;

      return this.mapTemplateFromDB(insertedData);
    } catch (error) {
      console.error("Error creating default template:", error);
      throw error;
    }
  }

  /**
   * Set a template as default
   */
  static async setAsDefault(templateId: string): Promise<void> {
    try {
      // First unset all other defaults for this repair shop
      const { data: template } = await supabase
        .from("invoice_templates")
        .select("repair_shop_id")
        .eq("id", templateId)
        .single();

      if (!template) {
        throw new Error("Template not found");
      }

      await this.unsetDefaultTemplates(templateId);

      // Set this template as default
      const { error } = await supabase
        .from("invoice_templates")
        .update({
          is_default: true,
          updated_at: new Date().toISOString(),
        })
        .eq("id", templateId);

      if (error) throw error;
    } catch (error) {
      console.error("Error setting template as default:", error);
      throw error;
    }
  }

  /**
   * Activate or deactivate a template
   */
  static async setTemplateStatus(
    templateId: string,
    isActive: boolean
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from("invoice_templates")
        .update({
          is_active: isActive,
          updated_at: new Date().toISOString(),
        })
        .eq("id", templateId);

      if (error) throw error;
    } catch (error) {
      console.error("Error updating template status:", error);
      throw error;
    }
  }

  /**
   * Get template usage statistics
   */
  static async getTemplateUsageStats(templateId: string): Promise<{
    invoiceCount: number;
    lastUsed?: Date;
    totalRevenue: number;
  }> {
    try {
      const { data: invoices, error } = await supabase
        .from("invoices")
        .select("created_at, total_amount")
        .eq("template_id", templateId);

      if (error) throw error;

      const invoiceCount = invoices.length;
      const lastUsed =
        invoices.length > 0
          ? new Date(
              Math.max(
                ...invoices.map((inv) => new Date(inv.created_at).getTime())
              )
            )
          : undefined;
      const totalRevenue = invoices.reduce(
        (sum, inv) => sum + inv.total_amount,
        0
      );

      return {
        invoiceCount,
        lastUsed,
        totalRevenue,
      };
    } catch (error) {
      console.error("Error fetching template usage stats:", error);
      throw error;
    }
  }

  /**
   * Validate template data
   */
  static validateTemplateData(
    data: Partial<InvoiceTemplateFormData>,
    isUpdate: boolean = false
  ): ValidationResult {
    const errors: ValidationError[] = [];

    // Required fields for creation
    if (!isUpdate) {
      if (!data.name || data.name.trim().length === 0) {
        errors.push({
          field: "name",
          message: "Template name is required",
          code: "REQUIRED",
        });
      }
    }

    // Name validation
    if (data.name !== undefined) {
      if (data.name.trim().length < 2) {
        errors.push({
          field: "name",
          message: "Template name must be at least 2 characters long",
          code: "MIN_LENGTH",
        });
      }
      if (data.name.trim().length > 100) {
        errors.push({
          field: "name",
          message: "Template name must be less than 100 characters",
          code: "MAX_LENGTH",
        });
      }
    }

    // Color validation
    const colorRegex = /^#[0-9A-F]{6}$/i;
    if (data.primaryColor && !colorRegex.test(data.primaryColor)) {
      errors.push({
        field: "primaryColor",
        message: "Primary color must be a valid hex color (e.g., #FF0000)",
        code: "INVALID_FORMAT",
      });
    }
    if (data.secondaryColor && !colorRegex.test(data.secondaryColor)) {
      errors.push({
        field: "secondaryColor",
        message: "Secondary color must be a valid hex color (e.g., #FF0000)",
        code: "INVALID_FORMAT",
      });
    }

    // Font size validation
    if (data.fontSize !== undefined) {
      if (data.fontSize < 8 || data.fontSize > 24) {
        errors.push({
          field: "fontSize",
          message: "Font size must be between 8 and 24",
          code: "OUT_OF_RANGE",
        });
      }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (data.companyEmail && !emailRegex.test(data.companyEmail)) {
      errors.push({
        field: "companyEmail",
        message: "Company email must be a valid email address",
        code: "INVALID_FORMAT",
      });
    }

    // URL validation
    const urlRegex = /^https?:\/\/.+/;
    if (data.companyWebsite && !urlRegex.test(data.companyWebsite)) {
      errors.push({
        field: "companyWebsite",
        message:
          "Company website must be a valid URL starting with http:// or https://",
        code: "INVALID_FORMAT",
      });
    }

    // Custom fields validation
    if (data.customFields) {
      data.customFields.forEach((field, index) => {
        if (!field.name || field.name.trim().length === 0) {
          errors.push({
            field: `customFields[${index}].name`,
            message: `Custom field ${index + 1} name is required`,
            code: "REQUIRED",
          });
        }
        if (!field.label || field.label.trim().length === 0) {
          errors.push({
            field: `customFields[${index}].label`,
            message: `Custom field ${index + 1} label is required`,
            code: "REQUIRED",
          });
        }
        if (
          !["text", "number", "date", "boolean", "select"].includes(field.type)
        ) {
          errors.push({
            field: `customFields[${index}].type`,
            message: `Custom field ${index + 1} has invalid type`,
            code: "INVALID_VALUE",
          });
        }
        if (
          field.type === "select" &&
          (!field.options || field.options.length === 0)
        ) {
          errors.push({
            field: `customFields[${index}].options`,
            message: `Custom field ${
              index + 1
            } of type 'select' must have options`,
            code: "REQUIRED",
          });
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Unset default flag for all templates except the specified one
   */
  private static async unsetDefaultTemplates(
    excludeId?: string
  ): Promise<void> {
    try {
      let query = supabase
        .from("invoice_templates")
        .update({
          is_default: false,
          updated_at: new Date().toISOString(),
        })
        .eq("is_default", true);

      if (excludeId) {
        query = query.neq("id", excludeId);
      }

      const { error } = await query;
      if (error) throw error;
    } catch (error) {
      console.error("Error unsetting default templates:", error);
      throw error;
    }
  }

  /**
   * Map template from database format to TypeScript interface
   */
  private static mapTemplateFromDB(dbRecord: any): InvoiceTemplate {
    return {
      id: dbRecord.id,
      name: dbRecord.name,
      description: dbRecord.description,
      isDefault: dbRecord.is_default,
      isActive: dbRecord.is_active,
      companyName: dbRecord.company_name,
      companyLogoUrl: dbRecord.company_logo_url,
      companyAddress: dbRecord.company_address,
      companyPhone: dbRecord.company_phone,
      companyEmail: dbRecord.company_email,
      companyWebsite: dbRecord.company_website,
      companyTaxId: dbRecord.company_tax_id,
      primaryColor: dbRecord.primary_color,
      secondaryColor: dbRecord.secondary_color,
      fontFamily: dbRecord.font_family,
      fontSize: dbRecord.font_size,
      showLogo: dbRecord.show_logo,
      showCompanyInfo: dbRecord.show_company_info,
      showTaxBreakdown: dbRecord.show_tax_breakdown,
      showPaymentTerms: dbRecord.show_payment_terms,
      showNotes: dbRecord.show_notes,
      customFields: dbRecord.custom_fields || [],
      footerText: dbRecord.footer_text,
      showPageNumbers: dbRecord.show_page_numbers,
      repairShopId: dbRecord.repair_shop_id,
      createdBy: dbRecord.created_by,
      createdAt: new Date(dbRecord.created_at),
      updatedAt: new Date(dbRecord.updated_at),
    };
  }

  /**
   * Map template to database format
   */
  private static mapTemplateToDB(template: InvoiceTemplateFormData): any {
    return {
      name: template.name,
      description: template.description,
      is_default: template.isDefault,
      is_active: template.isActive,
      company_name: template.companyName,
      company_logo_url: template.companyLogoUrl,
      company_address: template.companyAddress,
      company_phone: template.companyPhone,
      company_email: template.companyEmail,
      company_website: template.companyWebsite,
      company_tax_id: template.companyTaxId,
      primary_color: template.primaryColor,
      secondary_color: template.secondaryColor,
      font_family: template.fontFamily,
      font_size: template.fontSize,
      show_logo: template.showLogo,
      show_company_info: template.showCompanyInfo,
      show_tax_breakdown: template.showTaxBreakdown,
      show_payment_terms: template.showPaymentTerms,
      show_notes: template.showNotes,
      custom_fields: template.customFields,
      footer_text: template.footerText,
      show_page_numbers: template.showPageNumbers,
    };
  }
}
